import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { BrainCircuit, Network, Cpu, Share2, Sparkles, Binary, Copy, Fingerprint } from 'lucide-react';

const ArtificialIntelligence: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Neural Institute";
    
    return (
        <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-indigo-500 selection:text-white">
            {/* Top Navigation */}
            <nav className="fixed w-full z-50 px-8 py-6 bg-white/80 backdrop-blur-md border-b border-slate-100 flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-tr from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center text-white shadow-lg shadow-indigo-200">
                        <BrainCircuit size={18} />
                    </div>
                    <span className="font-bold text-lg tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-600">{schoolName}</span>
                </div>

                <div className="hidden md:flex gap-1 bg-slate-50 p-1 rounded-full border border-slate-200">
                    {['Research', 'Models', 'Ethics', 'About'].map(item => (
                        <a key={item} href="#" className="px-6 py-2 rounded-full text-sm font-medium text-slate-500 hover:bg-white hover:text-indigo-600 hover:shadow-sm transition-all">
                            {item}
                        </a>
                    ))}
                </div>

                <Button className="rounded-full bg-slate-900 text-white hover:bg-indigo-600 font-medium px-6 h-10 shadow-lg shadow-slate-200 transition-all hover:shadow-indigo-200">
                    Log In
                </Button>
            </nav>

            {/* Hero Section */}
            <header className="relative pt-32 pb-24 px-8 overflow-hidden">
                {/* Background Blobs (CSS) */}
                <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-indigo-100/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-purple-100/50 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>

                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-1.5 mb-8 shadow-sm">
                        <Sparkles size={14} className="text-indigo-500" />
                        <span className="text-xs font-semibold text-slate-600 tracking-wide">Defining the future of intelligence</span>
                    </div>
                    
                    <h1 className="text-6xl md:text-8xl font-bold tracking-tight text-slate-900 mb-8 leading-[1.1]">
                        Architecting <br/>
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500">Synthetic Minds.</span>
                    </h1>
                    
                    <p className="text-xl text-slate-500 max-w-2xl mx-auto mb-12 leading-relaxed">
                        Join the world's leading research center for artificial general intelligence, neural architecture search, and cognitive computing.
                    </p>
                    
                    <div className="flex justify-center gap-4">
                        <Button size="lg" className="rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold px-8 h-14 shadow-xl shadow-indigo-200 hover:scale-105 transition-transform">
                            Apply for Residency
                        </Button>
                        <Button size="lg" variant="outline" className="rounded-full border-slate-200 text-slate-600 hover:bg-slate-50 font-semibold px-8 h-14 bg-white shadow-sm hover:scale-105 transition-transform">
                            Read Manifesto
                        </Button>
                    </div>
                </div>
            </header>

            {/* Neural Network Graphic area */}
            <div className="max-w-6xl mx-auto px-8 mb-32">
                <div className="bg-white rounded-3xl border border-slate-100 shadow-2xl overflow-hidden aspect-[21/9] relative flex items-center justify-center">
                    <div className="absolute inset-0 bg-[linear-gradient(rgba(99,102,241,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(99,102,241,0.03)_1px,transparent_1px)] bg-[size:40px_40px]"></div>
                    
                    {/* Abstract node visualization */}
                    <div className="relative w-full h-full p-12 flex items-center justify-center gap-12">
                         <div className="w-24 h-24 rounded-2xl bg-white border border-slate-100 shadow-lg flex items-center justify-center animate-bounce">
                             <Binary className="text-indigo-500 block" size={32} />
                         </div>
                         <div className="h-px flex-1 bg-gradient-to-r from-indigo-200 to-purple-200 relative">
                             <div className="absolute top-1/2 left-0 w-2 h-2 bg-indigo-500 rounded-full -translate-y-1/2 animate-[ping_3s_linear_infinite]"></div>
                         </div>
                         <div className="w-24 h-24 rounded-2xl bg-white border border-slate-100 shadow-lg flex items-center justify-center animate-bounce delay-100">
                             <Cpu className="text-purple-500 block" size={32} />
                         </div>
                         <div className="h-px flex-1 bg-gradient-to-r from-purple-200 to-pink-200 relative">
                            <div className="absolute top-1/2 right-0 w-2 h-2 bg-pink-500 rounded-full -translate-y-1/2 animate-[ping_3s_linear_infinite]"></div>
                         </div>
                         <div className="w-24 h-24 rounded-2xl bg-white border border-slate-100 shadow-lg flex items-center justify-center animate-bounce delay-200">
                             <Network className="text-pink-500 block" size={32} />
                         </div>
                    </div>

                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-slate-900/90 backdrop-blur text-white px-4 py-2 rounded-full text-xs font-mono">
                         Status: Training Model v7.4 [Loss: 0.002]
                    </div>
                </div>
            </div>

            {/* Features Stacks */}
            <section className="bg-slate-50 py-24 px-8 border-t border-slate-100">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600">Core Disciplines</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { icon: Fingerprint, title: "Natural Language", desc: "Large language models and semantic understanding." },
                            { icon: Share2, title: "Computer Vision", desc: "Image recognition and generative synthesis." },
                            { icon: Copy, title: "Reinforcement", desc: "Autonomous agents and game theory optimization." },
                        ].map((item, i) => (
                            <div key={i} className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-indigo-100/50 transition-all hover:-translate-y-1 cursor-pointer group">
                                <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400 mb-6 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors">
                                    <item.icon size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                                <p className="text-slate-500 leading-relaxed text-sm">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-white py-20 px-8 border-t border-slate-100">
                 <div className="max-w-6xl mx-auto flex flex-col items-center">
                     <BrainCircuit size={32} className="text-slate-300 mb-8" />
                     <div className="text-slate-900 font-bold text-lg mb-2">{schoolName}</div>
                     <p className="text-slate-400 text-sm mb-8">San Francisco • London • Tokyo</p>
                     
                     <div className="flex gap-8 text-sm font-medium text-slate-500">
                         <a href="#" className="hover:text-indigo-600 transition-colors">Papers</a>
                         <a href="#" className="hover:text-indigo-600 transition-colors">Datasets</a>
                         <a href="#" className="hover:text-indigo-600 transition-colors">Careers</a>
                         <a href="#" className="hover:text-indigo-600 transition-colors">Press</a>
                     </div>
                     
                     <div className="mt-12 text-xs text-slate-300 font-mono">
                         v2.0.4 | © 2026 {schoolName}
                     </div>
                 </div>
            </footer>
        </div>
    );
};

export default ArtificialIntelligence;
